/**
 * Custom hook encapsulating the freehand-drawing canvas behaviour used by
 * AnnotationTool. Manages canvas refs, in-progress strokes, and the persisted
 * stroke list.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

export function useDrawingCanvas({ initialDrawings = [], color = '#000000', strokeWidth = 3, enabled = false }) {
  const canvasRef = useRef(null);
  const [drawings, setDrawings] = useState(initialDrawings);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);

  const startDrawing = (e) => {
    if (!enabled) return;
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    setCurrentStroke([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
  };

  const draw = (e) => {
    if (!isDrawing || !enabled) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setCurrentStroke((prev) => [...prev, point]);

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = color;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (currentStroke.length > 0) {
      const lastPoint = currentStroke[currentStroke.length - 1];
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (isDrawing && currentStroke.length > 1) {
      const stroke = {
        id: `stroke-${Date.now()}`,
        points: currentStroke,
        color,
        width: strokeWidth,
        tool: 'pen',
      };
      setDrawings((prev) => [...prev, stroke]);
    }
    setIsDrawing(false);
    setCurrentStroke([]);
  };

  const clearDrawings = () => {
    setDrawings([]);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    toast.success('Drawings cleared');
  };

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawings.forEach((stroke) => {
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (stroke.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        stroke.points.forEach((point) => ctx.lineTo(point.x, point.y));
        ctx.stroke();
      }
    });
  }, [drawings]);

  useEffect(() => {
    redrawCanvas();
  }, [drawings, redrawCanvas]);

  return {
    canvasRef, drawings, setDrawings,
    startDrawing, draw, stopDrawing, clearDrawings,
  };
}
